import React, { useContext, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import API from '../../../services/API'
import {
  CircularProgress,
  FormControl,
  Grid,
  LinearProgress,
  MenuItem,
  Select,
  InputLabel,
  Button,
  Alert,
} from '@mui/material'
import Administration from './Administration'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { GlobalContext, TenantSettingsContext } from '../../../App'
import { SiteModeContext } from '../../../components/mainFrame/MainFrame'

const AdministrationSelect = ({
  submitEPCRModule,
  epcrID,
  setSelectedModule,
  // Optional { medicineName, dose, time } - pre-selects the medicine and
  // time when linking a live-capture medicine stub to tracked stock
  prefill,
}) => {
  const theme = useTheme()
  const [scanQRcode, setScanQRcode] = useState(false)
  const [selectFromList, setSelectFromList] = useState(true) // switch to default false when QR working
  const [listOptions, setListOptions] = useState([])
  const [listLoading, setListLoading] = useState(true)
  const [selectedBag, setSelectedBag] = useState('')
  const [confirmedBag, setConfirmedBag] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const successSubmit = searchParams.get('success')
  const locationID = searchParams.get('location')
  const navigate = useNavigate()
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const { siteMode } = useContext(SiteModeContext)
  const medsAdministrationDisabled =
    orgSettings?.patientRecordSettings?.disabledMedicinesAdministrationForm &&
    siteMode !== 'ePCR'

  const handleSelectFromList = () => {
    setSelectFromList(true)
  }

  useEffect(() => {
    // Bags plus any store flagged as a mobile store (administered from
    // directly at events)
    API.get('/medsmx/bags?includeAdministrableStores=true')
      .then((res) => {
        setListOptions(res.data)
        if (locationID && res.data.some((x) => x._id === locationID)) {
          setSelectedBag(locationID)
          setConfirmedBag(locationID)
        }
      })
      .then(() => setListLoading(false))
      .catch((e) => console.log(e))
  }, [])

  if (medsAdministrationDisabled) {
    return (
      <Alert severity="error">
        Medicines Administration has been disabled for your organisation. Please
        record via an ePCR.
      </Alert>
    )
  }

  const ActionButton = ({ bgColor, text, onClick }) => (
    <Box
      width={200}
      height={50}
      sx={{
        background: theme.palette[bgColor].main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        borderRadius: 1,
        cursor: 'pointer',
      }}
      onClick={onClick}
    >
      {text}
    </Box>
  )

  return (
    <Box>
      {successSubmit ? (
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="title2" textAlign="center" fontSize="1rem">
            Administration successfully recorded
          </Typography>
          <Button variant="contained" onClick={() => navigate('')}>
            Record Another
          </Button>
          <Button variant="contained" onClick={() => navigate('/')}>
            Return Home
          </Button>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="title2" fontSize="1.4rem">
            Record a new drug administration
          </Typography>
          <Alert severity="warning">
            Please ensure you're selecting the correct bag and medicine.
            {epcrID
              ? ' You can amend or remove this administration from the treatment list while the ePCR remains in draft — once submitted it locks.'
              : ' Administrations cannot be undone.'}
          </Alert>
          {!confirmedBag ? (
            <>
              {/* TODO: <Grid container alignItems="center">
                <Grid
                  item
                  xs={12}
                  lg={5}
                  justifyContent={{ xs: 'center', lg: 'flex-end' }}
                  display="flex"
                >
                  <ActionButton
                    bgColor="anBlue"
                    text="Scan QR Code"
                    onClick={() => setScanQRcode(true)}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  lg={2}
                  justifyContent="center"
                  display="flex"
                >
                  <Typography variant="title2" fontSize="1.2rem">
                    - OR -
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  lg={5}
                  justifyContent={{ xs: 'center', lg: 'flex-start' }}
                  display="flex"
                >
                  <ActionButton
                    bgColor="anOrange"
                    text="Select Bag from List"
                    onClick={handleSelectFromList}
                  />
                </Grid>
              </Grid> */}
              {!listLoading && listOptions.length === 0 && (
                <Alert
                  severity="info"
                  action={
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => navigate('/site-configuration/medicines')}
                    >
                      Medicine Settings
                    </Button>
                  }
                >
                  No medicine bags exist yet, so there is nothing to record an
                  administration from. Create your bags (and stock them from a
                  medicine store) under Site Configuration → Governance →
                  Medicines — this needs Governance or site admin access.
                </Alert>
              )}
              <Box display="flex" justifyContent="center">
                {selectFromList && (
                  <FormControl fullWidth>
                    <InputLabel id="bagselect-label">
                      Select a bag or mobile store
                    </InputLabel>
                    <Select
                      labelId="bagselect-label"
                      id="bag"
                      value={selectedBag}
                      onChange={(e) => setSelectedBag(e.target.value)}
                      label="Select a bag or mobile store"
                    >
                      {listLoading ? (
                        <CircularProgress size="1rem" />
                      ) : (
                        listOptions.map((x) => (
                          <MenuItem key={x._id} value={x._id}>
                            {x.type === 'Medicine Store'
                              ? `${x.name} (Mobile store)`
                              : `${x?.name ?? x?.number} (${x?.level})`}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {/* Routed standalone there is no parent module to close —
                        only show Close when hosted inside the ePCR flow. */}
                    {setSelectedModule && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          setSelectedModule({})
                        }}
                        sx={{ marginTop: 2 }}
                      >
                        Close
                      </Button>
                    )}
                    {selectedBag && (
                      <Button
                        variant="contained"
                        color="info"
                        onClick={() => setConfirmedBag(selectedBag)}
                        sx={{ marginTop: 2 }}
                      >
                        Confirm
                      </Button>
                    )}
                  </FormControl>
                )}
              </Box>
            </>
          ) : (
            <Administration
              bagID={selectedBag}
              submitEPCRModule={submitEPCRModule}
              epcrID={epcrID}
              setSelectedModule={setSelectedModule}
              prefill={prefill}
            />
          )}
        </Box>
      )}
    </Box>
  )
}

export default AdministrationSelect
